import type React from 'react';
import {
  Action,
  AnyAction
} from 'redux';

// Utility Types

/**
 * To be used instead of `Record<TIndex, TValue>` when constructing an indexed object
 * of values, as `Record` is assumed to be exhaustive over `TIndex` by TypeScript.
 * See:
 * - https://github.com/elektron-technology/work-management-app/pull/172#discussion_r616806215
 * - https://fnune.com/typescript/2019/01/30/typescript-series-1-record-is-usually-not-the-best-choice/
 */
export type Indexed<
  TValue,
  TIndex extends string | number | symbol = string
> = { [ key in TIndex ]?: TValue };

/**
 * Convenience wrapper around the `Indexed<TValue, TIndex>` type, when the index type
 * comes from one of the value type's properties.
 */
export type IndexedBy<
  TValue extends { [key in TKey]: string | number | symbol },
  TKey extends string,
> = Indexed<TValue, TValue[TKey]>;

/** Extracts the props type of the given react component. */
export type PropsOf<TComponent extends (...args: any[]) => any>
  = TComponent extends React.FunctionComponent<infer TProps> ? TProps : never;

// Utility Functions

/**
 * Given a redux action and its type, returns a function which can be used to check
 * whether any redux action is of that type.
 */
export function isAction<TAction extends Action>(type: TAction['type']) {
  return (action: AnyAction): action is TAction => action.type === type;
}

export function generateString(length: number, alphabet = 'abcdefghijklmnopqrstuvwxyz0123456789') {
  let generated = '';
  while (generated.length < length) {
    const charIdx = Math.floor(Math.random( ) * alphabet.length);
    const char = alphabet[charIdx];
    generated += char;
  }
  return generated;
}
